import { useEffect, useState } from 'react';
import { useTtDetails } from '../../TtDetails';
import { useCashAccounts } from '../../CashAccount';
import { addMonetaryAmounts, decayedLimit, isDecayed } from '../../util';
import { latestDataPointGroupUpdateDate } from '../../DataPointGroup';
import {
  MonetaryAmount,
  MonetaryAmountDataPointGroup,
  useGetNoteReceivablesQuery,
  useGetPersonalPropertyItemsQuery,
} from '../../generated/graphql';
import { useInvestments } from '../../Investment';
import { useRealEstateProperties } from '../../RealEstateProperty';
import { useBusinesses } from '../../Business';
import { useLoans } from '../../Loan';
import { useGraphqlClient } from '../../graphql';

interface UseTotalAccountsDecayArgs {
  householdID: string;
}

export function useTotalAccountsDecay({ householdID }: UseTotalAccountsDecayArgs) {
  const [totalPPIDecayed, setTotalPPIDecayed] = useState<MonetaryAmount | null>(null);
  const [totalLoanDecayed, setTotalLoanDecayed] = useState<MonetaryAmount | null>(null);
  const [ratioOfDecayedValues, setRatioOfDecayedValues] = useState<number | null>(null);
  const [totalBusinessDecayed, setTotalBusinessDecayed] = useState<MonetaryAmount | null>(null);
  const [totalRealEstateDecayed, setTotalRealEstateDecayed] = useState<MonetaryAmount | null>(null);
  const [totalInvestmentDecayed, setTotalInvestmentDecayed] = useState<MonetaryAmount | null>(null);
  const [totalCashAccountDecayed, setTotalCashAccountDecayed] = useState<MonetaryAmount | null>(null);
  const [totalNoteReceivablesDecayed, setTotalNoteReceivablesDecayed] = useState<MonetaryAmount | null>(null);

  const { ttDetails, ttDetailsQuery } = useTtDetails({
    householdID,
    includeAssetSummary: true,
    includeDebtSummary: true,
  });
  const { loans, loansQuery } = useLoans({ householdID });
  const { businesses, businessesQuery } = useBusinesses({ householdID });
  const { investments, investmentsQuery } = useInvestments({ householdID });
  const { cashAccounts, cashAccountsQuery } = useCashAccounts({ householdID });
  const PPIsQuery = useGetPersonalPropertyItemsQuery(useGraphqlClient(), { householdID });
  const { realEstateProperties, realEstatePropertiesQuery } = useRealEstateProperties({ householdID });
  const noteReceivablesQuery = useGetNoteReceivablesQuery(useGraphqlClient(), { householdID });

  const totalAssetsDecayed = addMonetaryAmounts([
    totalCashAccountDecayed,
    totalInvestmentDecayed,
    totalRealEstateDecayed,
    totalBusinessDecayed,
    totalNoteReceivablesDecayed,
    totalPPIDecayed,
  ]);
  const totalDecayed = addMonetaryAmounts([totalLoanDecayed, totalAssetsDecayed]);
  const percentageOfDecayedValues = ratioOfDecayedValues ? Math.round(ratioOfDecayedValues * 100) : null;

  useEffect(() => {
    setTotalCashAccountDecayed(
      addMonetaryAmounts(
        cashAccounts
          .filter(({ value }) => isDecayed(latestDataPointGroupUpdateDate(value), decayedLimit.CashAccount))
          .map(({ ownedValue }) => ownedValue)
      )
    );
  }, [cashAccounts]);

  useEffect(() => {
    setTotalInvestmentDecayed(
      addMonetaryAmounts(
        investments
          .filter(({ value }) => isDecayed(latestDataPointGroupUpdateDate(value), decayedLimit.Investment))
          .map(({ ownedValue }) => ownedValue)
      )
    );
  }, [investments]);

  useEffect(() => {
    setTotalRealEstateDecayed(
      addMonetaryAmounts(
        realEstateProperties
          .filter(({ value }) => isDecayed(latestDataPointGroupUpdateDate(value), decayedLimit.RealEstate))
          .map(({ ownedValue }) => ownedValue)
      )
    );
  }, [realEstateProperties]);

  useEffect(() => {
    setTotalBusinessDecayed(
      addMonetaryAmounts(
        businesses
          .filter(({ value }) => isDecayed(latestDataPointGroupUpdateDate(value), decayedLimit.Business))
          .map(({ ownedValue }) => ownedValue)
      )
    );
  }, [businesses]);

  useEffect(() => {
    setTotalLoanDecayed(
      addMonetaryAmounts(
        loans
          .filter(({ balance }) => isDecayed(latestDataPointGroupUpdateDate(balance), decayedLimit.Loan))
          .map(({ ownedBalance }) => ownedBalance)
      )
    );
  }, [loans]);

  useEffect(() => {
    const noteReceivables = noteReceivablesQuery.data?.noteReceivables.items || [];
    setTotalNoteReceivablesDecayed(
      addMonetaryAmounts(
        noteReceivables
          .filter(({ value }) =>
            isDecayed(
              latestDataPointGroupUpdateDate(value as MonetaryAmountDataPointGroup),
              decayedLimit.NoteReceivable
            )
          )
          .map(({ ownedValue }) => ownedValue)
      )
    );
  }, [noteReceivablesQuery.data]);

  useEffect(() => {
    const PPIs = PPIsQuery.data?.personalPropertyItems.items || [];
    setTotalPPIDecayed(
      addMonetaryAmounts(
        PPIs.filter(({ value }) =>
          isDecayed(
            latestDataPointGroupUpdateDate(value as MonetaryAmountDataPointGroup),
            decayedLimit.PersonalPropertyItem
          )
        ).map(({ ownedValue }) => ownedValue)
      )
    );
  }, [PPIsQuery.data]);

  // We use the absolute total account values to calculate the ratio of decayed assets instead of the net worth
  // because the net worth is calculated by subtracting the total debt from the total assets, leading to a ratio
  // of decayed TOTAL account value that is not accurate.
  useEffect(() => {
    if (!ttDetails?.assetSummary.totalAssets.value || ttDetails.debtSummary.totalDebt.value || !totalDecayed?.value)
      return;
    const absoluteTotalAccountValues = ttDetails.assetSummary.totalAssets.value + ttDetails.debtSummary.totalDebt.value;
    setRatioOfDecayedValues(totalDecayed.value / absoluteTotalAccountValues);
  }, [ttDetails?.assetSummary, ttDetails?.debtSummary, totalDecayed]);

  return {
    totalDecayed,
    totalLoanDecayed,
    totalBusinessDecayed,
    totalInvestmentDecayed,
    totalRealEstateDecayed,
    totalCashAccountDecayed,
    totalNoteReceivablesDecayed,
    totalAssetsDecayed,
    totalPPIDecayed,
    ratioOfDecayedValues,
    percentageOfDecayedValues,
    isLoading:
      ttDetailsQuery.isLoading ||
      cashAccountsQuery.isLoading ||
      investmentsQuery.isLoading ||
      realEstatePropertiesQuery.isLoading ||
      businessesQuery.isLoading ||
      loansQuery.isLoading ||
      noteReceivablesQuery.isLoading ||
      PPIsQuery.isLoading,
  };
}
